import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Copy, CheckCircle2, Clock, Globe, Zap } from "lucide-react";

const STEPS = ["Add Domain", "Verification Record", "Validation", "Activate"];

export default function AddDomainWizard() {
  const [step, setStep] = useState(0);
  const [domain, setDomain] = useState("");
  const [record, setRecord] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [status, setStatus] = useState("pending");
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const createDomain = async () => {
    setError(null);
    try {
      const r = await api.post("/domains", { domain });
      setRecord(r.data);
      setStep(1);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to add domain");
    }
  };

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copied");
  };

  const checkDns = async () => {
    if (!record) return;
    setVerifying(true);
    try {
      const r = await api.post(`/domains/${record.id}/verify`);
      if (r.data.status === "verified") {
        setStatus("verified");
        setTimeout(() => setStep(3), 800);
      } else {
        setStatus("pending");
        toast.info("No matching TXT record yet — DNS may take a moment to propagate");
      }
    } catch {
      toast.error("Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const simulate = async () => {
    if (!record) return;
    setVerifying(true);
    setStatus("dns_detected");
    setTimeout(async () => {
      try {
        await api.post(`/domains/${record.id}/simulate-verify`);
        setStatus("verified");
        setTimeout(() => setStep(3), 700);
      } catch {
        toast.error("Simulation failed");
      } finally {
        setVerifying(false);
      }
    }, 1100);
  };

  const finish = () => navigate(`/dashboard/domains/${record.id}`);

  return (
    <div className="max-w-3xl mx-auto" data-testid="add-domain-wizard">
      <h1 className="font-display text-3xl font-bold ink">Add a new domain</h1>
      <p className="mt-2 ink-soft">Every domain must complete cryptographic ownership verification before telemetry ingestion is enabled.</p>

      {/* Stepper */}
      <div className="mt-10 flex items-center gap-3" data-testid="wizard-stepper">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2.5 ${i <= step ? "ink" : "ink-muted"}`}>
              <div className={`h-7 w-7 rounded-full border-2 flex items-center justify-center text-xs font-semibold ${
                i < step ? "bg-orange-500 border-orange-500 text-white"
                : i === step ? "border-orange-500 text-orange-600 bg-white"
                : "border-stone-300 bg-white"
              }`}>
                {i < step ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
              </div>
              <span className="text-xs font-medium hidden sm:inline">{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? "bg-orange-300" : "bg-stone-200"}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="mt-10 panel-shadow p-8 min-h-[400px]">
        {step === 0 && (
          <div data-testid="wizard-step-domain">
            <div className="h-12 w-12 rounded-xl bg-orange-50 flex items-center justify-center mb-5">
              <Globe className="h-6 w-6 text-orange-600" strokeWidth={2} />
            </div>
            <h2 className="font-display text-2xl font-bold ink">Enter your domain</h2>
            <p className="mt-2 ink-soft text-sm">Use the apex domain (e.g. example.com), not a subdomain.</p>
            <div className="mt-6">
              <input
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="example.com"
                className="w-full bg-white border border-stone-300 rounded-lg px-4 py-3.5 font-mono text-base ink focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 placeholder:text-stone-400"
                data-testid="wizard-domain-input"
              />
              {error && <div className="mt-3 text-sm text-red-700">{error}</div>}
            </div>
            <button
              onClick={createDomain}
              disabled={!domain.trim()}
              className="btn-accent mt-6 disabled:opacity-50"
              data-testid="wizard-next-1"
            >
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {step === 1 && record && (
          <div data-testid="wizard-step-record">
            <h2 className="font-display text-2xl font-bold ink">Add this TXT record</h2>
            <p className="mt-2 ink-soft text-sm">Add the following record to your DNS provider. Most providers propagate within minutes.</p>
            <div className="mt-6 grid sm:grid-cols-3 gap-3">
              <Field label="Type" value="TXT" />
              <Field label="Host" value={record.verification_host} copy={copy} />
              <Field label="TTL" value="300" />
            </div>
            <div className="mt-3">
              <Field label="Value" value={`trustdmarc-verification=${record.verification_token}`} copy={copy} />
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setStep(2)} className="btn-accent" data-testid="wizard-next-2">
                I've added the record <ArrowRight className="h-4 w-4" />
              </button>
              <button onClick={() => setStep(0)} className="btn-secondary" data-testid="wizard-back-2">Back</button>
            </div>
          </div>
        )}

        {step === 2 && record && (
          <div data-testid="wizard-step-validate">
            <h2 className="font-display text-2xl font-bold ink">Validating ownership</h2>
            <p className="mt-2 ink-soft text-sm">
              We'll query <span className="font-mono text-orange-600">{record.verification_host}</span> via DNS TXT and look for the verification token.
            </p>

            <div className="mt-6 grid grid-cols-4 gap-2">
              {["Pending", "DNS Detected", "Verified", "Telemetry On"].map((s, i) => {
                const reached =
                  (status === "pending" && i === 0) ||
                  (status === "dns_detected" && i <= 1) ||
                  (status === "verified" && i <= 2);
                return (
                  <div
                    key={s}
                    data-testid={`wizard-status-${i}`}
                    className={`text-center py-2.5 rounded-lg text-xs font-medium ${
                      reached ? "bg-orange-50 text-orange-700 border border-orange-200" : "bg-stone-50 text-stone-500 border border-stone-200"
                    }`}
                  >
                    {reached && i > 0 ? <CheckCircle2 className="inline h-3 w-3 mr-1" /> : null}
                    {s}
                  </div>
                );
              })}
            </div>

            <pre className="mt-6 font-mono text-xs text-stone-300 bg-[#0F0E0C] rounded-lg p-5 min-h-[120px] overflow-x-auto">
{`$ dig ${record.verification_host} TXT +short`}
              {verifying && <div className="text-orange-300 mt-1">resolving via edge worker…</div>}
              {status === "verified" && <div className="text-emerald-400 mt-1">"trustdmarc-verification={record.verification_token}" ✓</div>}
            </pre>

            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={checkDns} disabled={verifying} className="btn-accent disabled:opacity-50" data-testid="wizard-check-dns">
                {verifying ? <><Clock className="h-4 w-4 animate-spin" /> Checking…</> : <>Check DNS <ArrowRight className="h-4 w-4" /></>}
              </button>
              <button onClick={simulate} disabled={verifying} className="btn-secondary disabled:opacity-50" data-testid="wizard-simulate">
                <Zap className="h-4 w-4" /> Simulate (demo)
              </button>
            </div>
            <p className="mt-4 text-xs ink-muted">
              Tip: real DNS verification requires the TXT record to exist on your domain. Use simulate in demo environments.
            </p>
          </div>
        )}

        {step === 3 && record && (
          <div className="text-center py-6" data-testid="wizard-step-done">
            <div className="h-16 w-16 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" strokeWidth={2} />
            </div>
            <h2 className="font-display text-3xl font-bold ink">Domain verified</h2>
            <p className="mt-2 ink-soft">Telemetry ingestion is now enabled. Sender discovery and threat analysis have begun.</p>
            <button onClick={finish} className="btn-accent mt-7 mx-auto" data-testid="wizard-finish">
              View domain <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, copy }) {
  return (
    <div className="panel-soft p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider ink-muted">{label}</div>
      <div className="mt-1 flex items-center gap-2">
        <span className="font-mono text-sm ink truncate flex-1">{value}</span>
        {copy && (
          <button onClick={() => copy(value)} className="text-stone-400 hover:text-stone-900 p-1" data-testid={`copy-${label.toLowerCase()}`}>
            <Copy className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
